import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Lock, LockOpen, ExternalLink } from "lucide-react";
import { api, apiErrorText } from "@/lib/api";
import {
  VLKPageHeader, VLKInput, VLKSelect, VLKButton, VLKDataTable, VLKStatusBadge,
  VLKRiskMeter, VLKAlertDialog, VLKLabel, VLKTextarea, toast,
} from "@/components/vlk";
import { IDS } from "@/constants/testIds";

export default function Identities() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [lockState, setLockState] = useState("");
  const [riskLevel, setRiskLevel] = useState("");
  const [target, setTarget] = useState(null); // identity being locked/unlocked
  const [mode, setMode] = useState(null); // "lock" | "unlock"
  const [reason, setReason] = useState("");
  const [tier, setTier] = useState("owner");
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["identities", q, lockState, riskLevel],
    queryFn: () => api.get(`/identities`, { params: { q: q || undefined, lock_state: lockState || undefined, risk_level: riskLevel || undefined, limit: 100 } }).then(r => r.data),
  });

  const doLock = async () => {
    setBusy(true);
    try {
      await api.post("/lock", { vlk_id: target.vlk_id, reason, tier });
      toast.success(`${target.vlk_id} locked`, { description: `Tier: ${tier}` });
      qc.invalidateQueries({ queryKey: ["identities"] });
      qc.invalidateQueries({ queryKey: ["kpis"] });
      setTarget(null); setMode(null); setReason("");
    } catch (e) { toast.error("Lock failed", { description: apiErrorText(e) }); }
    finally { setBusy(false); }
  };

  const doUnlock = async () => {
    setBusy(true);
    try {
      await api.post("/unlock", { vlk_id: target.vlk_id, reason });
      toast.success(`${target.vlk_id} unlocked`);
      qc.invalidateQueries({ queryKey: ["identities"] });
      qc.invalidateQueries({ queryKey: ["kpis"] });
      setTarget(null); setMode(null); setReason("");
    } catch (e) { toast.error("Unlock failed", { description: apiErrorText(e) }); }
    finally { setBusy(false); }
  };

  const columns = [
    { key: "vlk_id", header: "VLK ID", mono: true, render: (r) => (
      <button
        onClick={(e) => { e.stopPropagation(); navigate(`/identities/${r.vlk_id}`); }}
        className="font-mono text-xs inline-flex items-center gap-1.5 hover:text-foreground text-muted-foreground group"
        data-testid={`identity-open-${r.vlk_id}`}
      >
        {r.vlk_id}
        <ExternalLink className="h-3 w-3 opacity-40 group-hover:opacity-100" />
      </button>
    ) },
    { key: "full_name", header: "Full name", render: (r) => (
      <button onClick={() => navigate(`/identities/${r.vlk_id}`)} className="font-medium hover:underline underline-offset-2">
        {r.full_name}
      </button>
    ) },
    { key: "email", header: "Email", render: (r) => <span className="text-muted-foreground text-xs">{r.email}</span> },
    { key: "verification_level", header: "Level", render: (r) => <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{r.verification_level}</span> },
    { key: "lock_state", header: "State", render: (r) => <VLKStatusBadge status={r.lock_state.toLowerCase()}>{r.lock_state}</VLKStatusBadge> },
    { key: "risk_level", header: "Risk", render: (r) => <VLKRiskMeter level={r.risk_level} /> },
    { key: "verifications_count", header: "Verifs", mono: true, align: "right", render: (r) => <span className="tabular">{r.verifications_count}</span> },
    {
      key: "actions", header: "", align: "right",
      render: (r) =>
        r.lock_state === "LOCKED" ? (
          <VLKButton
            data-testid={IDS.identityUnlockBtn(r.vlk_id)}
            variant="ghost" size="sm"
            onClick={() => { setTarget(r); setMode("unlock"); setReason(""); }}
          ><LockOpen className="h-3.5 w-3.5" />Unlock</VLKButton>
        ) : (
          <VLKButton
            data-testid={IDS.identityLockBtn(r.vlk_id)}
            variant="ghost" size="sm"
            onClick={() => { setTarget(r); setMode("lock"); setReason(""); setTier("owner"); }}
          ><Lock className="h-3.5 w-3.5" />Lock</VLKButton>
        ),
    },
  ];

  return (
    <div>
      <VLKPageHeader
        title="Identity registry"
        description="Search verified identities. Trigger Lock or Unlock workflows."
      />

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <VLKInput
            data-testid={IDS.identitySearch}
            placeholder="Search by VLK_ID, name or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
        <VLKSelect value={lockState} onChange={(e) => setLockState(e.target.value)} className="max-w-[160px]">
          <option value="">All states</option>
          <option value="ACTIVE">Active</option>
          <option value="LOCKED">Locked</option>
          <option value="PENDING">Pending</option>
        </VLKSelect>
        <VLKSelect value={riskLevel} onChange={(e) => setRiskLevel(e.target.value)} className="max-w-[160px]">
          <option value="">All risk levels</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </VLKSelect>
      </div>

      <VLKDataTable
        columns={columns}
        rows={data?.items || []}
        rowKey={(r) => r.vlk_id}
        rowTestId={(r) => IDS.identityRow(r.vlk_id)}
        empty={isLoading ? "Loading…" : "No identities match your filters."}
      />

      {/* Lock dialog */}
      <VLKAlertDialog
        open={mode === "lock" && !!target}
        onOpenChange={() => { setMode(null); setTarget(null); }}
        title={`Lock ${target?.vlk_id}`}
        tone="destructive"
        actions={
          <>
            <VLKButton variant="ghost" onClick={() => { setMode(null); setTarget(null); }}>Cancel</VLKButton>
            <VLKButton
              data-testid={IDS.lockSubmit}
              variant="destructive"
              onClick={doLock}
              loading={busy}
              disabled={reason.length < 3}
            >Confirm lock</VLKButton>
          </>
        }
      >
        <div className="space-y-3" data-testid={IDS.lockDialog}>
          <div className="text-sm text-foreground">
            You are about to <span className="text-destructive font-medium">lock</span>{" "}
            <span className="font-mono">{target?.full_name}</span>. This action is written to the immutable audit ledger.
          </div>
          <div className="space-y-1.5">
            <VLKLabel>Tier</VLKLabel>
            <VLKSelect data-testid={IDS.lockTierSelect} value={tier} onChange={(e) => setTier(e.target.value)}>
              <option value="owner">Owner (self)</option>
              <option value="institution">Institution</option>
              <option value="temporary">Temporary (24h)</option>
              <option value="emergency">Emergency (requires security_officer)</option>
              <option value="permanent">Permanent</option>
            </VLKSelect>
          </div>
          <div className="space-y-1.5">
            <VLKLabel>Reason (required)</VLKLabel>
            <VLKTextarea
              data-testid={IDS.lockReasonInput}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe the reason for the lock (auditable)…"
            />
          </div>
        </div>
      </VLKAlertDialog>

      {/* Unlock dialog */}
      <VLKAlertDialog
        open={mode === "unlock" && !!target}
        onOpenChange={() => { setMode(null); setTarget(null); }}
        title={`Unlock ${target?.vlk_id}`}
        actions={
          <>
            <VLKButton variant="ghost" onClick={() => { setMode(null); setTarget(null); }}>Cancel</VLKButton>
            <VLKButton
              data-testid={IDS.unlockSubmit}
              variant="primary"
              onClick={doUnlock}
              loading={busy}
              disabled={reason.length < 3}
            >Confirm unlock</VLKButton>
          </>
        }
      >
        <div className="space-y-3" data-testid={IDS.unlockDialog}>
          <div className="text-sm text-foreground">
            Unlock <span className="font-mono">{target?.full_name}</span>? This is auditable and reversible.
          </div>
          <div className="space-y-1.5">
            <VLKLabel>Reason (required)</VLKLabel>
            <VLKTextarea
              data-testid={IDS.unlockReasonInput}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for unlock…"
            />
          </div>
        </div>
      </VLKAlertDialog>
    </div>
  );
}
